import { createContext, useContext } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';

interface PropsTabella {
  /** Tracciato delle colonne, es. `90px 1.4fr 1fr 90px 130px`. */
  colonne: string;
  intestazioni: ReactNode[];
  children: ReactNode;
}

/**
 * Tabella a densità compatta dell'area amministrativa. Griglia CSS e non `<table>`: le
 * colonne devono restare allineate fra intestazione e righe anche quando una cella contiene
 * un badge o un gruppo di pulsanti.
 *
 * Il tracciato viene passato una volta e propagato alle righe tramite una variabile CSS.
 */
export function Tabella({ colonne, intestazioni, children }: PropsTabella) {
  const stile = { ['--colonne' as string]: colonne } as CSSProperties;
  return (
    <div className="tabella" style={stile} role="table">
      <div className="tabella__intestazione" style={{ gridTemplateColumns: colonne }} role="row">
        {intestazioni.map((testo, i) => (
          <div key={i} role="columnheader">
            {testo}
          </div>
        ))}
      </div>
      {children}
    </div>
  );
}

/**
 * Come si apre una riga.
 *
 * Passa per un contesto e non per una proprietà ripetuta cella per cella: chi scrive la
 * tabella dichiara `onApri` una volta sulla riga, e la cella che fa da maniglia — di norma
 * quella con il nome — lo raccoglie da sé. L'alternativa era ripetere la stessa funzione due
 * volte per ogni riga, dove la seconda prima o poi diverge dalla prima.
 */
const ContestoApertura = createContext<(() => void) | null>(null);

interface PropsRiga {
  colonne: string;
  /** Righe pari su superficie alternata. */
  alternata?: boolean;
  /**
   * Aprire questa riga: il dettaglio in modale, come per le accettazioni.
   *
   * Il clic vale su tutta la riga — è il bersaglio più grande che ci sia, e chi vuole
   * guardare un record punta il record, non un pulsante in fondo — **tranne** dove ci sono
   * comandi: lì il comando fa il suo mestiere e la riga non si apre alle sue spalle. La
   * distinzione si legge dal segnale del clic, non da un elenco di zone da tenere
   * aggiornato: se il clic è nato dentro un pulsante o un collegamento, non è per la riga.
   */
  onApri?: () => void;
  children: ReactNode;
}

export function Riga({ colonne, alternata = false, onApri, children }: PropsRiga) {
  const classi = [
    'tabella__riga',
    alternata && 'tabella__riga--alternata',
    onApri && 'tabella__riga--selezionabile',
  ]
    .filter(Boolean)
    .join(' ');

  const suClic = onApri
    ? (e: MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest('button, a, input, label')) return;
        onApri();
      }
    : undefined;

  return (
    <ContestoApertura.Provider value={onApri ?? null}>
      <div
        className={classi}
        style={{ gridTemplateColumns: colonne }}
        role="row"
        onClick={suClic}
      >
        {children}
      </div>
    </ContestoApertura.Provider>
  );
}

/**
 * La maniglia della riga: sembra il testo della cella, è un pulsante vero.
 *
 * Serve perché una riga che si apre al clic **non si apre con la tastiera**: un `div` con
 * un gestore non entra nel giro dei tabulatori e non risponde a Invio, e nessun `role` lo
 * sistema — un elemento con ruolo di pulsante non può contenere altri elementi attivabili,
 * e queste righe i pulsanti ce li hanno. Qui il bersaglio da tastiera è un elemento a sé,
 * dentro la cella che nomina il record, e per il mouse resta comodo tutto il resto.
 *
 * Fuori da una riga apribile è testo e basta: così la stessa cella si scrive nello stesso
 * modo nelle tabelle che non si aprono.
 */
export function Apri({ children }: { children: ReactNode }) {
  const apri = useContext(ContestoApertura);
  if (!apri) return <>{children}</>;
  return (
    <button type="button" className="tabella__apri" onClick={apri}>
      {children}
    </button>
  );
}

interface PropsCella {
  /** Monospaziato: ore, matricole, tessere, quantità. */
  dato?: boolean;
  secondaria?: boolean;
  children: ReactNode;
}

export function Cella({ dato = false, secondaria = false, children }: PropsCella) {
  const classi = [
    'tabella__cella',
    dato && 'tabella__cella--dato',
    secondaria && 'tabella__cella--secondaria',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classi} role="cell">
      {children}
    </div>
  );
}

interface VoceScheda {
  etichetta: string;
  valore: ReactNode;
  /** Monospaziato: date, matricole, tessere, quantità. */
  dato?: boolean;
  /**
   * Prende la riga intera invece di una colonna.
   *
   * Serve a due cose: ai valori lunghi, che in mezza scheda andrebbero a capo tre volte, e
   * a governare gli accoppiamenti. In una griglia a due colonne cinque voci si dispongono
   * 2-2-1, e due voci che vanno lette insieme — «Prima» e «Dopo» del registro — finiscono
   * in diagonale invece che affiancate. Una voce ampia prima di loro rimette il conto a
   * posto.
   */
  ampio?: boolean;
}

interface PropsSchedaElenco {
  titolo: ReactNode;
  /** Badge di stato, in alto a destra accanto al titolo. */
  insegna?: ReactNode;
  voci?: VoceScheda[];
  /** Pulsanti in fondo. Si dividono la riga in parti uguali. */
  azioni?: ReactNode;
  /** Aprire il dettaglio, come `Riga.onApri`: qui il bersaglio è la scheda intera. */
  onApri?: () => void;
}

/**
 * La riga di tabella, sul telefono.
 *
 * `Tabella` dispone le colonne con un tracciato a misure fisse — `1.5fr 1.3fr 100px 110px
 * 110px 1fr 150px` per i tiratori — e si porta dietro `overflow: hidden`. Sotto i 400 px
 * quel tracciato chiede 542 px dentro un contenitore che ne ha 343: le ultime colonne non
 * escono dai bordi, spariscono, e con loro spariva la colonna delle azioni. Non c'era
 * neanche una barra di scorrimento da trascinare per raggiungerla — il pulsante «Apri» era
 * disegnato, esisteva nel documento, e non era premibile in nessun modo.
 *
 * Una tabella stretta non è una tabella piccola: è un'altra forma. Qui ogni riga diventa una
 * scheda — titolo e stato in alto, i dati in coppie etichetta/valore, le azioni in fondo a
 * tutta larghezza — e nulla esce dai 375 px.
 */
export function SchedaElenco({
  titolo,
  insegna,
  voci,
  azioni,
  onApri,
}: PropsSchedaElenco) {
  const suClic = onApri
    ? (e: MouseEvent<HTMLElement>) => {
        if ((e.target as HTMLElement).closest('button, a, input, label')) return;
        onApri();
      }
    : undefined;

  return (
    <article
      className={`scheda-elenco${onApri ? ' scheda-elenco--apribile' : ''}`}
      onClick={suClic}
    >
      <div className="scheda-elenco__testa">
        <div className="scheda-elenco__titolo">
          {onApri ? (
            <button type="button" className="tabella__apri" onClick={onApri}>
              {titolo}
            </button>
          ) : (
            titolo
          )}
        </div>
        {insegna ? <div className="scheda-elenco__insegna">{insegna}</div> : null}
      </div>

      {voci && voci.length > 0 ? (
        <dl className="scheda-elenco__voci">
          {voci.map((v) => (
            <div
              key={v.etichetta}
              className={`scheda-elenco__voce${
                v.ampio ? ' scheda-elenco__voce--ampia' : ''
              }`}
            >
              <dt className="scheda-elenco__etichetta">{v.etichetta}</dt>
              <dd
                className={`scheda-elenco__valore${
                  v.dato ? ' scheda-elenco__valore--dato' : ''
                }`}
              >
                {v.valore}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {azioni ? <div className="scheda-elenco__azioni">{azioni}</div> : null}
    </article>
  );
}

/** Il contenitore delle schede: sostituisce `Tabella` quando lo schermo è stretto. */
export function ElencoSchede({ children }: { children: ReactNode }) {
  return <div className="elenco-schede">{children}</div>;
}

interface PropsRigaElenco {
  /** Ora d'ingresso, in evidenza a sinistra. */
  ora?: string;
  titolo: ReactNode;
  dettaglio?: ReactNode;
  azione?: ReactNode;
}

/**
 * Riga d'elenco a densità tattile. L'area DT non usa tabelle: righe alte, ora in
 * monospaziato a 22 px e pulsante d'azione a portata di pollice.
 */
export function RigaElenco({ ora, titolo, dettaglio, azione }: PropsRigaElenco) {
  return (
    <div className="riga-elenco">
      {ora ? <div className="riga-elenco__ora">{ora}</div> : null}
      <div className="riga-elenco__corpo">
        <div className="riga-elenco__titolo">{titolo}</div>
        {dettaglio ? <div className="riga-elenco__dettaglio">{dettaglio}</div> : null}
      </div>
      {azione}
    </div>
  );
}
